import { Component, Input, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { ChatFlowService, FlowCitation, FlowMessage } from '../services/chat.flow.service';

@Component({
    selector: 'flow-message-list',
    template: require('./message-list.component.html')
})
export class MessageListComponent implements OnChanges {

    @Input() project: any;
    @Input() topicId: string | null = null;
    @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef;

    messages: FlowMessage[] = [];
    loading = false;
    error: string | null = null;

    activeToolLabel: string | null = null;

    private toolCallTimer: any = null;
    private discardNextLoad = false;
    private streaming = false;
    private streamingContent = '';
    private renderFlushTimer: any = null;

    constructor(private chatFlowService: ChatFlowService) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.topicId) {
            this.streaming = false;
            this.discardNextLoad = false;
            if (changes.topicId.currentValue) {
                this.loadMessages();
            } else {
                this.messages = [];
                this.error = null;
            }
        }
    }

    loadMessages() {
        const requestedTopicId = this.topicId!;
        this.loading = true;
        this.error = null;
        this.chatFlowService.listMessages(this.project.unixName, requestedTopicId)
            .then(msgs => {
                if (this.topicId !== requestedTopicId) {
                    this.loading = false;
                    return;
                }
                if (this.discardNextLoad || this.streaming) {
                    this.discardNextLoad = false;
                    this.loading = false;
                    return;
                }
                this.messages = msgs.filter(m => m.role !== 'system');
                this.loading = false;
                this.scrollToBottom();
            })
            .catch(() => {
                if (this.topicId !== requestedTopicId) {
                    this.loading = false;
                    return;
                }
                this.discardNextLoad = false;
                this.error = 'Failed to load messages.';
                this.loading = false;
            });
    }

    // --- Called by ChatComponent during streaming ---

    skipLoad() {
        this.discardNextLoad = true;
    }

    addUserMessage(content: string) {
        const msg: FlowMessage = {
            id: Date.now(),
            topic_id: this.topicId || '',
            user_id: 0,
            role: 'user',
            content,
            meta: {},
            create_date: new Date().toISOString(),
        };
        this.messages = [...this.messages, msg];
        this.scrollToBottom();
    }

    addStreamingMessage() {
        this.streaming = true;
        this.streamingContent = '';
        const msg: FlowMessage = {
            id: -1,
            topic_id: this.topicId || '',
            user_id: 0,
            role: 'assistant',
            content: '',
            meta: {},
            create_date: new Date().toISOString(),
            streaming: true,
        };
        this.messages = [...this.messages, msg];
        this.scrollToBottom();
    }

    resetStreamingMessage() {
        if (this.renderFlushTimer) { clearTimeout(this.renderFlushTimer); this.renderFlushTimer = null; }
        this.streamingContent = '';
        const last = this.messages[this.messages.length - 1];
        if (!last || !last.streaming) { return; }
        this.messages = [...this.messages.slice(0, -1), { ...last, content: '' }];
    }

    appendToken(delta: string) {
        const last = this.messages[this.messages.length - 1];
        if (!last || !last.streaming) { return; }
        this.streamingContent += delta;
        if (!this.renderFlushTimer) {
            this.renderFlushTimer = setTimeout(() => this.flushStreamingContent(), 80);
        }
    }

    private flushStreamingContent() {
        this.renderFlushTimer = null;
        const last = this.messages[this.messages.length - 1];
        if (!last || !last.streaming) { return; }
        this.messages = [...this.messages.slice(0, -1), { ...last, content: this.streamingContent }];
        this.scrollToBottom();
    }

    startToolCall(_toolName: string, label: string) {
        if (this.toolCallTimer) { clearTimeout(this.toolCallTimer); }
        // Only surface the indicator if the tool takes longer than 1 second.
        // Fast tool calls complete invisibly; slow ones get contextual feedback.
        this.toolCallTimer = setTimeout(() => {
            this.activeToolLabel = label;
            this.toolCallTimer = null;
        this.scrollToBottom();
        }, 1000);
    }

    endToolCall(_toolName: string) {
        if (this.toolCallTimer) { clearTimeout(this.toolCallTimer); this.toolCallTimer = null; }
        this.activeToolLabel = null;
    }

    finalizeMessage(messageId: number, isError = false, citations?: FlowCitation[]) {
        if (this.renderFlushTimer) { clearTimeout(this.renderFlushTimer); this.renderFlushTimer = null; }
        if (this.toolCallTimer) { clearTimeout(this.toolCallTimer); this.toolCallTimer = null; }
        this.activeToolLabel = null;
        this.streaming = false;
        const last = this.messages[this.messages.length - 1];
        if (!last || !last.streaming) { return; }
        const updated = {
            ...last,
            content: this.streamingContent,
            streaming: false,
            id: messageId > 0 ? messageId : last.id,
            error: isError || undefined,
            citations: citations && citations.length > 0 ? citations : undefined,
        };
        this.messages = [...this.messages.slice(0, -1), updated];
    }

    scrollToBottom() {
        setTimeout(() => {
            if (this.scrollContainer) {
                const el = this.scrollContainer.nativeElement;
                el.scrollTop = el.scrollHeight;
            }
        }, 0);
    }

    trackMessage(_index: number, msg: FlowMessage): number | string {
        return msg.id !== -1 ? msg.id : msg.create_date;
    }
}
